import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Clock } from 'lucide-react';

// URL base da API
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '192.168.248.105') {
    return 'https://api-cms.cms20.online';
  }
  return 'https://api-cms.cms20.online';
};

interface Game {
  fixture: {
    id: number;
    date: string;
    status: {
      short: string;
      elapsed?: number;
    };
  };
  teams: {
    home: {
      id: number;
      name: string;
      logo?: string;
    };
    away: {
      id: number;
      name: string;
      logo?: string;
    };
  };
  goals: {
    home: number | null;
    away: number | null;
  };
  league: {
    id: number;
    name: string;
    country: string;
    logo?: string;
  };
}

interface CmsData {
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  gamesOfDay: {
    enabled: boolean;
    apiKey: string | null;
  };
}

const GamesOfDayPreview: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [cmsData, setCmsData] = useState<CmsData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) {
        setError('Slug do CMS não fornecido');
        setLoading(false);
        return;
      }

      try {
        const apiBaseUrl = getApiBaseUrl();
        const PROXY_BASE_URL = 'https://xms-proxy.u9ionsrv.workers.dev';
        const headers: any = {
          'Accept': 'application/json'
        };
        
        // Tentar primeiro com a API principal
        let gamesResponse;
        try {
          gamesResponse = await axios.get(`${apiBaseUrl}/api/cms/${slug}/games-of-day/data`, { headers, timeout: 10000 });
        } catch (error: any) {
          // Verificar se é erro de conexão/DNS/timeout OU erro 403 (acesso negado)
          // Erro 403 pode indicar bloqueio do domínio pelo cliente, então tentar proxy
          const isConnectionError = 
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED' ||
            error.code === 'ETIMEDOUT' ||
            error.message?.includes('Network Error') ||
            error.message?.includes('timeout') ||
            error.message?.includes('ECONNREFUSED') ||
            error.message?.includes('ENOTFOUND');
          
          const isForbidden = error.response?.status === 403;
          const isServerError = error.response?.status >= 500 && error.response?.status < 600;
          
          // Tentar proxy se for erro de conexão, 403 (acesso negado) ou erro do servidor
          if (isConnectionError || isForbidden || isServerError) {
            const errorType = isForbidden ? '403 (acesso negado)' : 
                             isServerError ? `${error.response?.status} (erro do servidor)` : 
                             'de conexão';
            
            console.warn(`⚠️ [GamesOfDay] Erro ${errorType} com API principal. Tentando proxy...`);
            // Tentar com a proxy como fallback
            try {
              gamesResponse = await axios.get(`${PROXY_BASE_URL}/api/cms/${slug}/games-of-day/data`, { 
                headers, 
                timeout: 10000,
                withCredentials: true 
              });
              console.log(`✅ [GamesOfDay] Dados obtidos via proxy com sucesso`);
            } catch (proxyError: any) {
              console.error('❌ [GamesOfDay] Erro ao buscar dados via proxy:', proxyError);
              throw error; // Se a proxy também falhar, lançar erro original
            }
          } else {
            throw error; // Se não for erro que justifica usar proxy, lançar erro original
          }
        }
        
        if (gamesResponse.data && gamesResponse.data.success) {
          setCmsData({
            name: gamesResponse.data.cms?.name || 'CMS',
            colors: gamesResponse.data.cms?.colors || {
              primary: '#3B82F6',
              secondary: '#1E40AF',
              accent: '#F59E0B',
              background: '#F0F9FF',
              text: '#1F2937'
            },
            gamesOfDay: { enabled: true, apiKey: null }
          });
          const gamesArray = Array.isArray(gamesResponse.data.games) ? gamesResponse.data.games : [];
          setGames(gamesArray);
        } else {
          setError(gamesResponse.data?.error || 'Erro ao buscar jogos do dia');
        }
      } catch (err: any) {
        console.error('Erro ao buscar jogos:', err);
        
        if (err.response) {
          if (err.response.status === 404) {
            setError(err.response.data?.error || 'Rota não encontrada. Verifique se o CMS está configurado corretamente.');
          } else if (err.response.status === 401) {
            setError('API Key inválida ou expirada');
          } else if (err.response.status === 429) {
            setError('Limite de requisições excedido. Tente novamente mais tarde.');
          } else if (err.response.status === 500) {
            setError(err.response.data?.error || 'Erro interno do servidor. Tente novamente mais tarde.');
          } else if (err.response.data?.error) {
            setError(err.response.data.error);
          } else {
            setError('Erro ao buscar jogos do dia. Tente novamente mais tarde.');
          }
        } else if (err.request) {
          setError('Erro de conexão. Verifique sua internet e tente novamente.');
        } else {
          setError(err.message || 'Erro ao buscar jogos do dia. Tente novamente mais tarde.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const colors = cmsData?.colors || {
    primary: '#3B82F6',
    secondary: '#1E40AF',
    accent: '#F59E0B',
    background: '#F0F9FF',
    text: '#1F2937'
  };

  /**
   * Verificar se o jogo está ao vivo baseado no horário atual
   */
  const getStatusInfo = (game: Game) => {
    const status = game.fixture?.status?.short || '';
    const gameDate = game.fixture?.date ? new Date(game.fixture.date) : null;
    const now = new Date();
    
    // Verificar se tem placar (indica que o jogo começou)
    const hasScore = game.goals.home !== null && game.goals.away !== null;
    
    // Verificar se o jogo já começou (data/hora passou)
    const hasStarted = gameDate && gameDate <= now;
    
    // Calcular minutos desde o início do jogo
    let minutesSinceStart = 0;
    if (gameDate && hasStarted) {
      minutesSinceStart = Math.floor((now.getTime() - gameDate.getTime()) / (1000 * 60));
    }
    
    // Status explícitos da API - AO VIVO
    if (status === 'LIVE' || status === '1H' || status === '2H' || status === 'HT' || status === 'ET' || status === 'P') {
      return { text: 'AO VIVO', class: 'live', bgColor: '#ef4444' };
    }
    
    // Status explícitos da API - FINALIZADO
    if (['FT', 'AET', 'PEN', 'CANC', 'ABAN', 'AWARDED', 'WO'].includes(status)) {
      return { text: 'FINALIZADO', class: 'finished', bgColor: '#6b7280' };
    }
    
    // Se o jogo já começou e está dentro do horário de jogo (até 105 minutos)
    if (hasStarted && minutesSinceStart >= 0 && minutesSinceStart <= 105) {
      // Se tem placar, está ao vivo
      if (hasScore) {
        return { text: 'AO VIVO', class: 'live', bgColor: '#ef4444' };
      }
      // Se não tem placar mas está dentro do horário, pode estar ao vivo
      if (minutesSinceStart < 120) {
        return { text: 'AO VIVO', class: 'live', bgColor: '#ef4444' };
      }
    }
    
    // Se passou mais de 105 minutos, provavelmente finalizou
    if (hasStarted && minutesSinceStart > 105) {
      return { text: 'FINALIZADO', class: 'finished', bgColor: '#6b7280' };
    }
    
    // Jogo ainda não começou
    return { text: 'PRÓXIMO', class: 'upcoming', bgColor: '#FF6B9D' };
  };

  const getTimeText = (game: Game) => {
    const status = game.fixture?.status?.short || '';
    const gameDate = game.fixture?.date ? new Date(game.fixture.date) : null;
    const now = new Date();
    const hasScore = game.goals.home !== null && game.goals.away !== null;
    const hasStarted = gameDate && gameDate <= now;
    
    // Status explícitos de ao vivo
    if (status === 'LIVE' || status === '1H' || status === '2H' || status === 'HT' || status === 'ET' || status === 'P') {
      return `${game.fixture?.status?.elapsed || 0}'`;
    }
    
    // Status explícitos de finalizado
    if (['FT', 'AET', 'PEN', 'CANC', 'ABAN', 'AWARDED', 'WO'].includes(status)) {
      return 'Finalizado';
    }
    
    // Se o jogo já começou e tem placar, mostrar tempo decorrido
    if (hasStarted && hasScore) {
      if (gameDate) {
        const minutesSinceStart = Math.floor((now.getTime() - gameDate.getTime()) / (1000 * 60));
        if (minutesSinceStart > 105) {
          return 'Finalizado';
        }
        return `${minutesSinceStart}'`;
      }
    }
    
    // Se o jogo já começou mas não tem placar ainda, calcular tempo decorrido
    if (hasStarted && !hasScore) {
      if (gameDate) {
        const minutesSinceStart = Math.floor((now.getTime() - gameDate.getTime()) / (1000 * 60));
        if (minutesSinceStart > 105) {
          return 'Finalizado';
        }
        return `${minutesSinceStart}'`;
      }
    }
    
    // Jogo ainda não começou, mostrar horário
    if (gameDate) {
      return gameDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    
    return '';
  };

  const getGameDate = (game: Game) => {
    const gameDate = game.fixture?.date ? new Date(game.fixture.date) : null;
    if (!gameDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const gameDateOnly = new Date(gameDate);
    gameDateOnly.setHours(0, 0, 0, 0);
    
    const diffTime = gameDateOnly.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Hoje';
    } else if (diffDays === 1) {
      return 'Amanhã';
    } else {
      return gameDate.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
        }}
      >
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg">Carregando jogos do dia...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
        }}
      >
        <div className="bg-white rounded-lg p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-bold mb-2 text-gray-800">Erro ao carregar jogos</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Ordenar jogos: ao vivo primeiro, depois próximos, depois finalizados
  const sortedGames = [...games].sort((a, b) => {
    const statusA = getStatusInfo(a);
    const statusB = getStatusInfo(b);
    
    if (statusA.text === 'AO VIVO' && statusB.text !== 'AO VIVO') return -1;
    if (statusA.text !== 'AO VIVO' && statusB.text === 'AO VIVO') return 1;
    if (statusA.text === 'PRÓXIMO' && statusB.text === 'FINALIZADO') return -1;
    if (statusA.text === 'FINALIZADO' && statusB.text === 'PRÓXIMO') return 1;
    
    // Se ambos são ao vivo, ordenar por tempo decorrido
    if (statusA.text === 'AO VIVO' && statusB.text === 'AO VIVO') {
      const timeA = getTimeText(a);
      const timeB = getTimeText(b);
      const minutesA = parseInt(timeA.replace("'", "")) || 0;
      const minutesB = parseInt(timeB.replace("'", "")) || 0;
      return minutesB - minutesA; // Mais tempo primeiro
    }
    
    return 0;
  });

  return (
    <div
      className="min-h-screen p-3 md:p-4"
      style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
      }}
    >
      <div className="max-w-[1920px] mx-auto">
        {/* Header compacto - removido o header cinza grande */}
        <div className="flex items-center justify-between mb-4 text-white">
          <div>
            <h1 className="text-lg md:text-xl font-bold">Jogos do Dia</h1>
            <p className="text-xs md:text-sm opacity-90">
              {new Date().toLocaleDateString('pt-BR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
            Principais Ligas
          </div>
        </div>

        {/* Games Grid - Cards menores para exibir mais jogos */}
        {sortedGames.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 text-center text-white">
            <h2 className="text-xl font-bold mb-2">Nenhum jogo encontrado para hoje</h2>
            <p className="text-sm opacity-80">Verifique se há jogos agendados para a data atual.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2 md:gap-3">
            {sortedGames.map((game) => {
              const statusInfo = getStatusInfo(game);
              const isLive = statusInfo.text === 'AO VIVO';
              const isFinished = statusInfo.text === 'FINALIZADO';

              return (
                <div
                  key={game.fixture.id}
                  className={`bg-white rounded-lg p-2 md:p-3 shadow-md transition-all duration-200 ${
                    isLive ? 'ring-2 ring-red-400 shadow-red-200' : ''
                  }`}
                  style={{
                    borderLeft: `3px solid ${isLive ? '#ef4444' : isFinished ? '#6b7280' : '#FF6B9D'}`
                  }}
                >
                  {/* Status Badge - Compacto */}
                  <div className="mb-1.5 flex items-center justify-between gap-1">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] md:text-[10px] font-bold text-white whitespace-nowrap ${
                        isLive ? 'animate-pulse' : ''
                      }`}
                      style={{ backgroundColor: statusInfo.bgColor }}
                    >
                      {statusInfo.text}
                    </span>
                    {getGameDate(game) && (
                      <span className="text-[9px] text-gray-500 font-medium whitespace-nowrap">
                        {getGameDate(game)}
                      </span>
                    )}
                  </div>

                  {/* League Info - Compacto */}
                  <div className="text-[9px] md:text-[10px] text-gray-600 mb-1.5 font-medium truncate" title={`${game.league.name} - ${game.league.country}`}>
                    {game.league.name}
                  </div>

                  {/* Teams - Layout Compacto */}
                  <div className="flex items-center justify-between mb-1.5 gap-1">
                    <div className="flex-1 text-center min-w-0">
                      {game.teams.home.logo && (
                        <img
                          src={game.teams.home.logo}
                          alt={game.teams.home.name}
                          className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-0.5 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="font-bold text-[9px] md:text-[10px] truncate" title={game.teams.home.name}>
                        {game.teams.home.name}
                      </div>
                    </div>

                    <div
                      className="text-sm md:text-base font-bold mx-1 whitespace-nowrap"
                      style={{ color: colors.primary }}
                    >
                      {game.goals.home !== null && game.goals.away !== null
                        ? `${game.goals.home}-${game.goals.away}`
                        : 'VS'}
                    </div>

                    <div className="flex-1 text-center min-w-0">
                      {game.teams.away.logo && (
                        <img
                          src={game.teams.away.logo}
                          alt={game.teams.away.name}
                          className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-0.5 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div className="font-bold text-[9px] md:text-[10px] truncate" title={game.teams.away.name}>
                        {game.teams.away.name}
                      </div>
                    </div>
                  </div>

                  {/* Time - Compacto */}
                  <div className="text-center text-[9px] md:text-[10px] text-gray-600 flex items-center justify-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{getTimeText(game)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GamesOfDayPreview;
